import { useEffect, useRef, useState } from 'react';
import { useDebouncedValue } from './useDebouncedValue';

/**
 * useServerAutosave — debounced, server-only autosave for forms.
 *
 * Per spec §4.8 + §4.9: no localStorage / sessionStorage / IndexedDB.
 * Form state lives in React + on the server. If a user closes the tab
 * before the debounce fires, the last ~few keystrokes are lost.
 *
 * Usage:
 *   const autosave = useServerAutosave({
 *     values: form.watch(),
 *     saveFn: (data) => upsertCustomerDraft(data),
 *     enabled: !!loadedDraft,            // skip until the draft has loaded
 *     debounceMs: 800,
 *   });
 *
 *   // Drive the status pill UI:
 *   <DraftStatusPill state={autosave.state} />
 *
 * Status state values:
 *   - 'idle'        nothing has changed since load
 *   - 'saving'      a request is in flight
 *   - 'saved'       last save succeeded (will revert to idle after `successHoldMs`)
 *   - 'error'       last save failed (stays until next attempt succeeds)
 */
export type AutosaveState = 'idle' | 'saving' | 'saved' | 'error';

export interface UseServerAutosaveOptions<T> {
  values: T;
  saveFn: (values: T) => Promise<void> | Promise<unknown>;
  enabled?: boolean;
  debounceMs?: number;
  /** How long the "Saved" indicator lingers before reverting to idle (ms). */
  successHoldMs?: number;
  /**
   * Validate one field before saving it (e.g. `(names) => form.trigger(names)`).
   * Called once per CHANGED top-level key, each time with a single-element
   * array, so a caller can keep the plain `form.trigger` passthrough.
   *
   * Invalid fields are not blocked from the tick — they're EXCLUDED from it.
   * Each invalid key is swapped back to its last-saved value and everything
   * else persists immediately. This matters because the save is a full-document
   * replace: gating the whole tick on validity meant one abandoned invalid
   * field (a half-typed DUNS, say) silently stalled autosave for every other
   * field until it was fixed, since the failing key stayed in the changed set
   * forever. Now only that field is held back, and it saves on the tick where
   * it becomes valid.
   */
  validateFields?: (changedKeys: string[]) => boolean | Promise<boolean>;
  /**
   * Called instead of the default whole-key revert when a top-level key
   * fails `validateFields`. Lets an ARRAY-typed key (e.g. a `useFieldArray`
   * list) hold back only its broken item(s) instead of the entire array —
   * `changedTopLevelKeys` reports one array field as a single key, so
   * without this, ONE invalid row (say, row 3 of 10) reverts every row,
   * including unrelated edits made to rows 1-2 in the same tick.
   *
   * Called once per invalid key, AFTER `validateFields` has resolved for
   * it, so it's safe to read fresh validation state (e.g. `form.formState
   * .errors`) inside. Return the merged value to send; return `undefined`
   * to drop the key entirely (mirrors the no-previous-value default).
   */
  mergeInvalidField?: (key: string, previous: unknown, next: unknown) => unknown;
}

export interface UseServerAutosaveReturn {
  state: AutosaveState;
  lastSavedAt: Date | null;
  lastError: Error | null;
}

export function useServerAutosave<T>({
  values,
  saveFn,
  enabled = true,
  debounceMs = 800,
  successHoldMs = 2000,
  validateFields,
  mergeInvalidField,
}: UseServerAutosaveOptions<T>): UseServerAutosaveReturn {
  const debounced = useDebouncedValue(values, debounceMs);

  const [state, setState] = useState<AutosaveState>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [lastError, setLastError] = useState<Error | null>(null);

  // Serialization of the last INPUT we processed, so we skip the PUT when
  // nothing actually changed. React Hook Form's `watch()` returns a fresh
  // object every render, so reference equality alone would fire saves on every
  // render. JSON.stringify is fine here — the wizard payloads are small
  // (~100 fields) and the comparison runs at most every `debounceMs`.
  // Tracked separately from what we SENT: when an invalid field is held back,
  // the payload deliberately differs from the input, and comparing against the
  // payload would make every render look like new input and re-save forever.
  const lastSerializedRef = useRef<string | null>(null);
  // The values we last actually SENT — the baseline for the changed-keys diff
  // and the source of the last-known-good value for a held-back field.
  const lastValuesRef = useRef<T | null>(null);
  // Latch the initial debounced value: skip the FIRST effect run so we
  // don't PUT on mount with the just-hydrated defaults.
  const hasMountedRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;

    const serialized = stableSerialize(debounced);

    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      // Latch the baseline from the CURRENT `values`, not the lagging `debounced`. Forms that
      // mount empty and hydrate via effect (product editor: `data`/`name` start null, then
      // `enabled` flips true before the debounce settles) would otherwise latch a stale baseline
      // (the pre-hydration value) and fire one spurious save of unchanged data on load. Callers
      // whose `enabled` depends on data that hydrates via a SEPARATE effect (`useHydrateOnce`)
      // must gate `enabled` on that hydration having actually landed — see `useHydrateOnce`'s
      // returned flag — not on the raw "data is loaded" condition, or `enabled` flips true one
      // render before `values` reflects the hydrated record and this still latches empty.
      lastSerializedRef.current = stableSerialize(values);
      lastValuesRef.current = values;
      return;
    }

    if (serialized === lastSerializedRef.current) {
      // Reference changed (RHF reissues `watch()` per render) but the
      // content is identical. Skip the PUT.
      return;
    }

    let cancelled = false;

    (async () => {
      let payload = debounced;

      payload = await buildAutosavePayload({
        next: debounced,
        previous: lastValuesRef.current,
        validateFields,
        mergeInvalidField,
        isAborted: () => cancelled,
      });
      if (cancelled) return;

      // Every changed key was invalid — the payload is what's already on the
      // server. Record the input as seen so we don't reprocess it, and wait for
      // the next edit (typically the fix) to try again. Only reachable when
      // something was actually held back, which is what `payload !== debounced`
      // says (the builder returns its input by reference otherwise).
      if (
        payload !== debounced &&
        lastValuesRef.current &&
        stableSerialize(payload) === stableSerialize(lastValuesRef.current)
      ) {
        lastSerializedRef.current = serialized;
        return;
      }

      setState('saving');
      setLastError(null);

      try {
        await saveFn(payload);
        if (cancelled) return;
        lastSerializedRef.current = serialized;
        lastValuesRef.current = payload;
        setState('saved');
        setLastSavedAt(new Date());
        setTimeout(() => {
          if (!cancelled) setState((s) => (s === 'saved' ? 'idle' : s));
        }, successHoldMs);
      } catch (err) {
        if (cancelled) return;
        // Don't update lastSerializedRef on failure — next attempt should
        // retry with the latest payload, including any further edits.
        setState('error');
        setLastError(err instanceof Error ? err : new Error(String(err)));
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced, enabled]);

  return { state, lastSavedAt, lastError };
}

/**
 * Build the payload to send for one autosave tick, holding invalid fields back.
 *
 * Each changed top-level key is validated on its own; an invalid one reverts to
 * its last-saved value so the full-document replace can't persist it, while every
 * other change in the tick still goes out. A key with no last-saved value was
 * never validly set, so it's dropped rather than sent.
 *
 * Exported because the hook is NOT the only thing that sends a payload: a save
 * deferred behind an in-flight request is flushed later by `save-coordinator`,
 * and anything reading `form.getValues()` at that point would walk straight past
 * this hold-back and persist the half-typed value the guard exists to stop.
 * Every sender must route through here rather than reimplement it.
 *
 * Returns `next` BY REFERENCE when nothing was held back, so callers can tell the
 * two cases apart without re-serializing.
 */
export async function buildAutosavePayload<T>({
  next,
  previous,
  validateFields,
  mergeInvalidField,
  isAborted,
}: {
  next: T;
  previous: T | null;
  validateFields?: (changedKeys: string[]) => boolean | Promise<boolean>;
  mergeInvalidField?: (key: string, previous: unknown, next: unknown) => unknown;
  /** Checked between per-key validations so an unmounted caller stops early. */
  isAborted?: () => boolean;
}): Promise<T> {
  if (!validateFields) return next;

  const invalid: string[] = [];
  for (const key of changedTopLevelKeys(previous, next)) {
    const ok = await validateFields([key]);
    if (isAborted?.()) return next;
    if (!ok) invalid.push(key);
  }
  if (invalid.length === 0) return next;

  const prev = previous as Record<string, unknown> | null;
  const held = { ...(next as Record<string, unknown>) };
  for (const key of invalid) {
    if (mergeInvalidField) {
      const merged = mergeInvalidField(
        key,
        prev ? prev[key] : undefined,
        (next as Record<string, unknown>)[key],
      );
      if (merged === undefined) delete held[key];
      else held[key] = merged;
    } else if (prev && key in prev) {
      held[key] = prev[key];
    } else {
      delete held[key];
    }
  }
  return held as T;
}

/**
 * Merge several autosave states into ONE for a shared status pill. The customer
 * wizard's base/US autosave and the EU module's own jurisdiction autosave both
 * feed the single header pill, so it lights up whichever market is saving.
 * Priority: any saving → saving; else any error → error; else any recently
 * saved → saved; else idle. lastSavedAt is the most recent across all parts.
 */
export function combineAutosave(...parts: UseServerAutosaveReturn[]): UseServerAutosaveReturn {
  const state: AutosaveState = parts.some((p) => p.state === 'saving')
    ? 'saving'
    : parts.some((p) => p.state === 'error')
      ? 'error'
      : parts.some((p) => p.state === 'saved')
        ? 'saved'
        : 'idle';
  const lastSavedAt = parts.reduce<Date | null>(
    (acc, p) => (p.lastSavedAt && (!acc || p.lastSavedAt > acc) ? p.lastSavedAt : acc),
    null,
  );
  const lastError = parts.find((p) => p.state === 'error')?.lastError ?? null;
  return { state, lastSavedAt, lastError };
}

/**
 * JSON.stringify with sorted keys so an object whose keys are added in a
 * different order serializes to the same string. RHF mutates the value's
 * internal structure as nested fields update; sorted keys make the
 * dedupe deterministic.
 */
function stableSerialize(value: unknown): string {
  return JSON.stringify(value, (_k, v) => {
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      const sorted: Record<string, unknown> = {};
      for (const k of Object.keys(v as Record<string, unknown>).sort()) {
        sorted[k] = (v as Record<string, unknown>)[k];
      }
      return sorted;
    }
    return v;
  });
}

/**
 * Top-level keys whose serialized value differs between `prev` and `next`.
 * Used to hand `validateFields` only the fields the user actually touched —
 * a value nested inside a changed key (e.g. one row of an array field) is
 * reported as that whole top-level key, which matches how `form.trigger`
 * validates a field name.
 */
function changedTopLevelKeys<T>(prev: T | null, next: T): string[] {
  if (typeof next !== 'object' || next === null) return [];
  if (typeof prev !== 'object' || prev === null) {
    return Object.keys(next as Record<string, unknown>);
  }
  const prevObj = prev as Record<string, unknown>;
  const nextObj = next as Record<string, unknown>;
  const keys = new Set([...Object.keys(prevObj), ...Object.keys(nextObj)]);
  const changed: string[] = [];
  for (const key of keys) {
    if (stableSerialize(prevObj[key]) !== stableSerialize(nextObj[key])) {
      changed.push(key);
    }
  }
  return changed;
}
